import { isSameDay } from 'date-fns';
import type { StateCreator } from 'zustand';
import { create } from 'zustand';

import { FIRST_OF_JANUARY_2019 } from '@izimi/modules';

import type { Filters, FilterStoreSlices, SharedSlice } from './documentFilters/types';
import { createStoreHook } from './helpers';

const filtersInitialState = {
  filters: {
    labels: [],
    ownerIds: [],
    receiverIds: [],
    sources: [],
    onlySoonToBeTrashed: false,
    afterCreationDate: FIRST_OF_JANUARY_2019,
    beforeCreationDate: new Date(),
  },
};

const createSlice =
  (
    type: keyof FilterStoreSlices,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): StateCreator<FilterStoreSlices, [], [], any> =>
  (set, get) => ({
    [type]: {
      ...filtersInitialState,
      setFilters: (filters: Filters) => set({ [type]: { ...get()[type], filters } }),
      resetFilters: () =>
        set({
          [type]: { ...get()[type], filters: filtersInitialState.filters },
        }),
      hasActiveFilters: () => {
        const { filters } = get()[type];
        return (
          filters.labels.length > 0 ||
          filters.ownerIds.length > 0 ||
          filters.receiverIds.length > 0 ||
          filters.sources.length > 0 ||
          !isSameDay(filters.afterCreationDate, FIRST_OF_JANUARY_2019) ||
          !isSameDay(filters.beforeCreationDate, new Date()) ||
          filters.onlySoonToBeTrashed
        );
      },
      onlyUnseen: () => {
        const { filters } = get()[type];
        return !!filters.unSeen;
      },
    },
  });

const createVaultSlice = createSlice('vault');
const createFromContactsSlice = createSlice('fromContacts');
const createByYouSlice = createSlice('byYou');

const useBoundStore = create<FilterStoreSlices & SharedSlice>()((...a) => {
  const [set, get] = a;

  return {
    ...createVaultSlice(...a),
    ...createFromContactsSlice(...a),
    ...createByYouSlice(...a),
    shared: {
      isSearchModeActive: false,
      setIsSearchModeActive: (isSearchModeActive: boolean) =>
        set({ shared: { ...get().shared, isSearchModeActive } }),
      setSearchValue: (searchValue: string) => set({ shared: { ...get().shared, searchValue } }),
      searchValue: '',
      resetStore: () => {
        const { byYou, fromContacts, vault } = get();
        byYou.resetFilters();
        fromContacts.resetFilters();
        vault.resetFilters();
        set({
          shared: {
            ...get().shared,
            searchValue: '',
            isSearchModeActive: false,
          },
        });
      },
    },
  };
});

export const useDocumentFiltersStore = (type: keyof FilterStoreSlices) => {
  const store = useBoundStore();

  return {
    ...store[type],
    ...store.shared,
  };
};

const createStoreHookSelector = createStoreHook(useBoundStore);

export const useDocumentFiltersSearchValue = createStoreHookSelector(
  (state) => state.shared.searchValue,
);
